/**
 * Phase 7 Commit J. Done step — confetti + ring-pulse + display-font
 * date. The conversion is sealed; the screen reads as a celebration,
 * not a 200-OK.
 */

import type { Booking } from '../types';
import { CheckIcon } from '../icons';

interface Props {
  booking: Booking;
  timezone: string;
  allowCustomerCancel: boolean;
  locationPhone?: string;
  /** Customer first name for personalised header copy. */
  customerName?: string;
  /** Phase 7 — D58. Tenant-configured copy appended below the default. */
  confirmationCopy?: string | null;
  /** Optional staff name for the "with {staff}" line. */
  staffName?: string | null;
  /** Optional location address for the "Pull up to..." line. */
  locationAddress?: string | null;
  /**
   * D71 — a card was saved this session in sb-vault card-on-file mode. Says,
   * in writing, which card the salon will charge and for how much (or that
   * nothing is charged unless the policy is triggered). Null = no card saved.
   */
  cardOnFile?: {
    last4: string | null;
    depositCents: number;
    currency: string;
    merchantName: string;
  } | null;
}

function formatCardMoney(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

export function DoneStep({
  booking,
  timezone,
  allowCustomerCancel,
  locationPhone,
  customerName,
  confirmationCopy,
  staffName,
  locationAddress,
  cardOnFile,
}: Props) {
  const dt = new Date(booking.startAt);
  const dayLabel = dt.toLocaleString('en-US', {
    weekday: 'long',
    timeZone: timezone,
  });
  const timeLabel = dt.toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
  });

  return (
    <div class="sbw-done-stage">
      <div class="sbw-confetti" aria-hidden="true">
        <span /><span /><span /><span /><span /><span />
      </div>
      <div class="sbw-done-mark">
        <CheckIcon size={28} />
      </div>
      <div class="sbw-done-eyebrow">You&apos;re booked</div>
      <h2 class="sbw-done-title">
        See you {dayLabel}
        {customerName ? `, ${customerName}` : ''}.
      </h2>
      <div class="sbw-done-when">
        {timeLabel}
        {staffName ? ` · with ${staffName}` : ''}
      </div>
      {/* No "confirmation text is on its way" promise — not every provider
          sends one (Meevo doesn't text API-channel bookings; notes.md #24).
          Tenant-specific confirmation copy still renders below via D58. */}
      <p class="sbw-done-sub">
        You&apos;re all set.
        {locationAddress ? ` Pull up to ${locationAddress} a few min early.` : ''}
      </p>

      {cardOnFile && (
        <p class="sbw-confirmation-copy">
          {cardOnFile.depositCents > 0
            ? `A ${formatCardMoney(cardOnFile.depositCents, cardOnFile.currency)} deposit will be charged to your card${
                cardOnFile.last4 ? ` ending ${cardOnFile.last4}` : ''
              } by ${cardOnFile.merchantName} shortly after booking. It comes off your total on the day.`
            : `Your card${cardOnFile.last4 ? ` ending ${cardOnFile.last4}` : ''} is saved with ${cardOnFile.merchantName}. Nothing is charged today — only for a late cancellation or no-show, per the policy you accepted.`}
        </p>
      )}

      {/* The tenant's generic confirmation copy is suppressed when the
          session-specific card-on-file sentence above already says it —
          Adelaide configures both, and two deposit notices read as a stutter. */}
      {!cardOnFile && confirmationCopy && (
        <p class="sbw-confirmation-copy">{confirmationCopy}</p>
      )}

      {!allowCustomerCancel && locationPhone && (
        <p class="sbw-cancel-note">
          Need to cancel or reschedule? Call{' '}
          <a href={`tel:${locationPhone}`}>{locationPhone}</a>.
        </p>
      )}

      <div class="sbw-done-id">BOOKING ID · {booking.id}</div>
    </div>
  );
}
